import React, { useEffect, useState } from 'react';
import { X, BookOpen, Calculator, Sparkles } from 'lucide-react';

interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: 'welcome' | 'overview' | 'calculations';
}

export const TutorialModal: React.FC<TutorialModalProps> = ({ isOpen, onClose, defaultTab = 'overview' }) => {
  const [activeTab, setActiveTab] = useState<'welcome' | 'overview' | 'calculations'>(defaultTab);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab);
    }
  }, [isOpen, defaultTab]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div
        className="relative bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col"
        style={{ boxShadow: '0 18px 42px rgba(247, 201, 72, 0.22)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-end p-6 border-b border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close tutorial"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 flex-shrink-0">
          <button
            onClick={() => setActiveTab('welcome')}
            className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'welcome'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            Welcome
          </button>
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'overview'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('calculations')}
            className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'calculations'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Calculator className="w-4 h-4" />
            Detailed Calculations
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {activeTab === 'welcome' ? (
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <div className="space-y-2">
                <h3 className="text-2xl font-semibold text-gray-900">Welcome!</h3>
                <p>
                  This calculator simulates the relative <strong>net worth</strong> outcomes of renting and buying scenarios.
                </p>
              </div>
              <div className="space-y-3">
                <p className="font-semibold text-gray-900">To get started:</p>
                <ol className="list-decimal space-y-2 pl-6">
                  <li>
                    Create a minimum of two scenarios. Select one of them as your baseline.
                    <p className="mt-1 text-sm italic text-gray-600">
                      Note: Only buying scenarios can be baselines scenarios because their constants are used to seed the opportunity cost calculations for renting scenarios.
                    </p>
                  </li>
                  <li>Adjust the parameters of the scenarios.</li>
                </ol>
              </div>
              <p>
                The calculator will display the net worth of each scenario <em>relative</em> to the baseline scenario.
              </p>
              <p>For more information, visit the 'How it Works' tab!</p>
            </div>
          ) : activeTab === 'overview' ? (
            <div className="space-y-8">
              {/* Section 1: Motivation */}
            <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Why This Calculator?</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <p className="text-gray-700 leading-relaxed">
                    Most rent vs buy calculators miss a crucial factor: the <strong>opportunity cost of your down payment</strong>. 
                    When you buy, you lock up a large sum of money that could otherwise be invested in the stock market. 
                    This calculator accounts for that opportunity cost by comparing your net worth in both scenarios over time.
                  </p>
                </div>
              </div>

              {/* Section 2: Baseline & Chart Explanation */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">How the Analysis Works</h3>
                <div className="space-y-4">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Baseline Concept</h4>
                    <p className="text-gray-700 leading-relaxed">
                      To make comparisons meaningful, you star a <strong>buying scenario</strong> as your baseline (typically your most realistic option). 
                      All other scenarios are then plotted relative to this buying baseline, showing how much better or worse each alternative performs.
                    </p>
            </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Chart Interpretation</h4>
                    <p className="text-gray-700 leading-relaxed">
                      The chart displays <strong>"Net Worth vs Baseline"</strong> over your selected time period. 
                      The baseline is always the buying scenario you selected, so the zero line reflects that ownership path.
                      <span className="text-green-600 font-medium"> Positive values</span> indicate a scenario performs better than your baseline; 
                      <span className="text-red-600 font-medium"> negative values</span> indicate worse performance. 
                      The baseline itself always shows as a flat line at $0.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 3: What's Included vs Excluded */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">What's Included in Our Analysis</h3>
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h4 className="font-semibold text-green-900 mb-3">✅ What We Include</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-green-800 text-sm">
                      <div>
                        <p className="font-medium mb-2">For Buying:</p>
                        <ul className="space-y-1 ml-4">
                          <li>• Down payment and closing costs</li>
                          <li>• Monthly mortgage payments (P&I)</li>
                          <li>• Property taxes and insurance</li>
                          <li>• PMI and HOA fees</li>
                          <li>• Maintenance costs (1% annually)</li>
                          <li>• Property appreciation</li>
                          <li>• FHA UFMIP (if applicable)</li>
                          <li>• Rate buydown costs (if enabled)</li>
                          <li>• Rental income (if renting out property)</li>
                          <li>• Tax benefits and capital gains</li>
                        </ul>
                      </div>
                      <div>
                        <p className="font-medium mb-2">For Renting:</p>
                        <ul className="space-y-1 ml-4">
                          <li>• Monthly rent payments</li>
                          <li>• Renters insurance (if enabled)</li>
                          <li>• Security deposits and broker fees (if enabled)</li>
                          <li>• Investment returns on saved money</li>
                          <li>• Opportunity cost of down payment</li>
                          <li>• Monthly savings from lower costs</li>
                          <li>• Sublease income (if applicable)</li>
                          <li>• Investment tax implications</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h4 className="font-semibold text-blue-900 mb-3">ℹ️ What We Exclude</h4>
                    <p className="text-blue-800 text-sm mb-3">
                      To keep the analysis focused on core financial factors, we exclude certain costs that vary significantly by location and circumstances:
                    </p>
                    <ul className="text-blue-800 text-sm space-y-1 ml-4">
                      <li>• Real estate agent commissions (typically 5-6% of home value)</li>
                      <li>• Home inspection, appraisal, and survey fees</li>
                      <li>• Title insurance and escrow fees</li>
                      <li>• Moving costs and temporary housing</li>
                      <li>• Staging and marketing expenses when selling</li>
                      <li>• Home warranty and utility setup costs</li>
                      <li>• Furniture and home improvement expenses</li>
                    </ul>
                    <p className="text-blue-800 text-sm mt-3">
                      <strong>Why exclude these?</strong> These one-time costs vary dramatically by market, timing, and individual circumstances. 
                      For a more complete analysis, consider adding 6-8% of the home value to your upfront costs.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 4: Sample Scenario Walkthrough */}
            <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Understanding a Sample Analysis</h3>
                <div className="space-y-4">
                  <div className="bg-white border-2 border-gray-200 rounded-lg p-4 shadow-sm">
                    <img 
                      src="/example_scenario.png" 
                      alt="Sample scenario analysis showing 1900 Chestnut St vs 201 Willow Ave"
                      className="w-full rounded-lg shadow-sm"
                    />
                  </div>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h4 className="font-semibold text-green-900 mb-3">Scenario Breakdown</h4>
                    <div className="space-y-3 text-green-800">
                      <p>
                        <strong>Properties:</strong> 1900 Chestnut St (buying, set as baseline shown in blue) vs 201 Willow Ave (renting, shown in red)
                      </p>
                      <p>
                        <strong>Result:</strong> Over 11 years, renting at 201 Willow Ave performs approximately <span className="font-bold">$229,895 better</span> than buying 1900 Chestnut St.
                      </p>
                      <p>
                        <strong>What's Included:</strong> This accounts for investing the down payment money in the market, ongoing rent vs mortgage costs, property appreciation, and all associated fees.
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h4 className="font-semibold text-blue-900 mb-3">How to Use the Calculator</h4>
                    <p className="text-blue-800">
                      Adjust the sliders below to match your specific situation: home price, interest rate, down payment, rent amount, and market assumptions. 
                      The analysis will update in real-time to show you the optimal financial decision for your circumstances.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Section 4: Detailed Calculations */}
                <div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  <h3 className="text-2xl font-semibold text-blue-900 mb-3">🧮 How We Calculate Net Worth</h3>
                  <p className="text-blue-800">
                    This section provides a comprehensive breakdown of how we calculate net worth for both buying and renting scenarios. 
                    We'll walk through each step with real examples to help you understand exactly how your money grows (or shrinks) over time.
                  </p>
                </div>
                
                {/* Calculation Overview */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">📊 The Big Picture</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white p-4 rounded-lg border">
                      <h5 className="font-semibold text-gray-900 mb-2">🏠 Buying Scenario</h5>
                      <p className="text-sm text-gray-700">
                        Your net worth = <strong>Home Equity</strong> + <strong>Invested Rental Surplus</strong>
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Home equity grows as you pay down the mortgage and the property appreciates, while any rental profit that exceeds the monthly housing payment is invested alongside your cash reserves.
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border">
                      <h5 className="font-semibold text-gray-900 mb-2">🏢 Renting Scenario</h5>
                      <p className="text-sm text-gray-700">
                        Your net worth = <strong>Investment Balance</strong>
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        The investment balance already reflects rent payments—monthly contributions drop when rent is higher than the buying baseline, and they increase when renting is cheaper.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Buying Scenario Calculations */}
                <div className="mb-8">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">🏠 Buying Scenario: Step-by-Step</h4>
                  
                  <div className="space-y-6">
                    {/* Step 1: Upfront Costs */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center mb-3">
                        <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">1</span>
                        <h5 className="font-semibold text-gray-900">Upfront Costs (Cash to Close)</h5>
                      </div>
                      <p className="text-gray-700 text-sm mb-3">
                        This is the money you need to bring to the closing table. It's the foundation of your buying investment.
                      </p>
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto mb-3">
                        <pre>{`Cash to Close = Down Payment + Closing Costs + Agent Fees 
                   + UFMIP (for FHA) - Seller Credits - Lender Credits`}</pre>
                      </div>
                      <div className="bg-white p-4 rounded-lg border">
                        <h6 className="font-semibold text-gray-900 mb-2">Example: $500,000 Home</h6>
                        <div className="text-sm text-gray-700 space-y-1">
                          <p>• Down Payment (10%): $50,000</p>
                          <p>• Closing Costs: $4,000</p>
                          <p>• Agent Fees: $0 (buyer doesn't pay)</p>
                          <p>• UFMIP (FHA): $0 (conventional loan)</p>
                          <p>• Seller Credits: $0</p>
                          <p>• <strong>Total Cash to Close: $54,000</strong></p>
                        </div>
                      </div>
                    </div>

                    {/* Step 2: Monthly Costs */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center mb-3">
                        <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">2</span>
                        <h5 className="font-semibold text-gray-900">Monthly Housing Costs</h5>
                      </div>
                      <p className="text-gray-700 text-sm mb-3">
                        Your gross housing payment bundles the mortgage, taxes, insurance, HOA, and PMI. Rental income offsets this bill; any dollars left over after paying the housing costs are invested each month.
                      </p>
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto mb-3">
                        <pre>{`Gross Housing Cost = Principal & Interest + Property Tax + HOA + Insurance + PMI
Net Monthly Cost = Gross Housing Cost - Rental Income
Invested Rental Surplus = max(0, Rental Income - Gross Housing Cost)`}</pre>
                      </div>
                      <div className="bg-white p-4 rounded-lg border">
                        <h6 className="font-semibold text-gray-900 mb-2">Example: $500,000 Home (30-year, 6.5%)</h6>
                        <div className="text-sm text-gray-700 space-y-1">
                          <p>• Mortgage Payment: $2,528 (P&I)</p>
                          <p>• Property Tax: $521 (1.25% annually)</p>
                          <p>• HOA: $0</p>
                          <p>• Insurance: $200</p>
                          <p>• PMI: $208 (0.5% annually)</p>
                          <p>• Rental Income: $3,800 (roommates/ADU)</p>
                          <p>• <strong>Net Monthly Cost: -$343 (invested as surplus)</strong></p>
                        </div>
                      </div>
                    </div>

                    {/* Step 3: Equity Growth */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center mb-3">
                        <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">3</span>
                        <h5 className="font-semibold text-gray-900">Home Equity Growth</h5>
                      </div>
                      <p className="text-gray-700 text-sm mb-3">
                        Your home equity grows in two ways: paying down the mortgage (forced savings) and property appreciation.
                      </p>
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto mb-3">
                        <pre>{`Home Equity = Current Home Value - Remaining Mortgage Balance
Net Equity = Home Equity - Selling Costs - Capital Gains Tax`}</pre>
                      </div>
                      <div className="bg-white p-4 rounded-lg border">
                        <h6 className="font-semibold text-gray-900 mb-2">Example: After 5 Years</h6>
                        <div className="text-sm text-gray-700 space-y-1">
                          <p>• Home Value (4% appreciation): $608,326</p>
                          <p>• Remaining Mortgage: $401,674</p>
                          <p>• Home Equity: $206,652</p>
                          <p>• Selling Costs (6%): $36,500</p>
                          <p>• Capital Gains Tax: $0 (primary residence)</p>
                          <p>• <strong>Net Equity: $170,152</strong></p>
                        </div>
                      </div>
                    </div>

                    {/* Step 4: Net Worth Calculation */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center mb-3">
                        <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">4</span>
                        <h5 className="font-semibold text-gray-900">Buying Net Worth</h5>
                      </div>
                      <p className="text-gray-700 text-sm mb-3">
                        Your buying net worth blends the proceeds you would keep after selling with any rental profit that was invested along the way. We still track the cash you spent, but surplus rent is credited back through its own investment balance instead of being counted twice as a cost.
                      </p>
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto mb-3">
                        <pre>{`Buying Net Worth = Net Equity + Surplus Investment Balance

Surplus Investment Balance = Sum of invested rental profit (compounded monthly)`}</pre>
                      </div>
                      <div className="bg-white p-4 rounded-lg border">
                        <h6 className="font-semibold text-gray-900 mb-2">Example: After 5 Years</h6>
                        <div className="text-sm text-gray-700 space-y-1">
                          <p>• Net Equity: $170,152</p>
                          <p>• Surplus Investment Balance: $21,600</p>
                          <p>• <strong>Buying Net Worth: $191,752</strong></p>
                          <p className="text-xs text-gray-600 mt-2">
                            Cash outflows are still reported separately, but invested surplus offsets part of that spending.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Renting Scenario Calculations */}
                <div className="mb-8">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">🏢 Renting Scenario: Step-by-Step</h4>
                  
                  <div className="space-y-6">
                    {/* Step 1: Initial Investment */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center mb-3">
                        <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">1</span>
                        <h5 className="font-semibold text-gray-900">Initial Investment</h5>
                      </div>
                      <p className="text-gray-700 text-sm mb-3">
                        Instead of buying a home, you invest the money you would have used for a down payment. This is your starting investment.
                      </p>
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto mb-3">
                        <pre>{`Initial Investment = Cash to Close (from buying scenario)
                       - Security Deposit - Broker Fees`}</pre>
                      </div>
                      <div className="bg-white p-4 rounded-lg border">
                        <h6 className="font-semibold text-gray-900 mb-2">Example: $500,000 Home</h6>
                        <div className="text-sm text-gray-700 space-y-1">
                          <p>• Cash to Close (from buying): $54,000</p>
                          <p>• Security Deposit: $3,500</p>
                          <p>• Broker Fees: $0</p>
                          <p>• <strong>Initial Investment: $50,500</strong></p>
                        </div>
                      </div>
                    </div>

                    {/* Step 2: Monthly Cash Flow */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center mb-3">
                        <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">2</span>
                        <h5 className="font-semibold text-gray-900">Monthly Cash Flow</h5>
                      </div>
                      <p className="text-gray-700 text-sm mb-3">
                        Each month, you save the difference between what buying would cost and what renting actually costs. This becomes your monthly investment contribution.
                      </p>
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto mb-3">
                        <pre>{`Monthly Savings = (Hypothetical Buying Cost + Maintenance) - Actual Rent`}</pre>
                      </div>
                      <div className="bg-white p-4 rounded-lg border">
                        <h6 className="font-semibold text-gray-900 mb-2">Example: Month 1</h6>
                        <div className="text-sm text-gray-700 space-y-1">
                          <p>• Buying Cost: $3,457 (mortgage + taxes + insurance)</p>
                          <p>• Maintenance: $417 (1% annually)</p>
                          <p>• Total Buying Cost: $3,874</p>
                          <p>• Actual Rent: $3,500</p>
                          <p>• <strong>Monthly Savings: $374</strong></p>
                        </div>
                      </div>
                    </div>

                    {/* Step 3: Investment Growth */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center mb-3">
                        <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">3</span>
                        <h5 className="font-semibold text-gray-900">Investment Growth</h5>
                      </div>
                      <p className="text-gray-700 text-sm mb-3">
                        Your investment grows through compound returns and monthly contributions. We use monthly compounding for accuracy.
                      </p>
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto mb-3">
                        <pre>{`For each month:
  Investment Balance += Monthly Savings
  Investment Balance *= (1 + Monthly Return Rate)

Monthly Return Rate = (1 + Annual Return Rate)^(1/12) - 1`}</pre>
                      </div>
                      <div className="bg-white p-4 rounded-lg border">
                        <h6 className="font-semibold text-gray-900 mb-2">Example: After 5 Years (7% annual return)</h6>
                        <div className="text-sm text-gray-700 space-y-1">
                          <p>• Initial Investment: $50,500</p>
                          <p>• Monthly Contributions: $374</p>
                          <p>• Total Contributions: $72,940</p>
                          <p>• Investment Growth: $45,560</p>
                          <p>• <strong>Total Investment Value: $169,000</strong></p>
                        </div>
                      </div>
                    </div>

                    {/* Step 4: Net Worth Calculation */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center mb-3">
                        <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">4</span>
                        <h5 className="font-semibold text-gray-900">Renting Net Worth</h5>
                      </div>
                      <p className="text-gray-700 text-sm mb-3">
                        Your renting net worth is simply the value of your investment account. Rent payments are already baked in because monthly contributions shrink whenever rent is higher than the owning baseline.
                      </p>
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto mb-3">
                        <pre>{`Renting Net Worth = Investment Balance`}</pre>
                      </div>
                      <div className="bg-white p-4 rounded-lg border">
                        <h6 className="font-semibold text-gray-900 mb-2">Example: After 5 Years</h6>
                        <div className="text-sm text-gray-700 space-y-1">
                          <p>• Investment Balance: $169,000</p>
                          <p>• Total Rent Paid (tracked separately): $210,000</p>
                          <p>• <strong>Renting Net Worth: $169,000</strong></p>
                          <p className="text-xs text-gray-600 mt-2">
                            Rent is already reflected in the investment balance because monthly contributions drop when rent is expensive.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Relative Comparison */}
                <div className="mb-8">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">⚖️ Relative Comparison</h4>
                  
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <h5 className="font-semibold text-blue-900 mb-3">Net Worth vs Baseline</h5>
                      <p className="text-blue-800 text-sm mb-3">
                        The chart shows how each scenario performs relative to your chosen baseline. This makes it easy to compare different options.
                      </p>
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                        <pre>{`Net Worth vs Baseline = Scenario Net Worth - Baseline Net Worth`}</pre>
                      </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <h5 className="font-semibold text-green-900 mb-3">Sample Calculation (Year 5)</h5>
                      <div className="space-y-2 text-green-800">
                        <p>• <strong>Buying:</strong> $170,152 net equity - $207,420 cash out = -$37,268</p>
                        <p>• <strong>Renting:</strong> $169,000 invested - $210,000 rent paid = -$41,000</p>
                        <p>• <strong>Relative to buying:</strong> -$41,000 - (-$37,268) = <span className="font-bold text-red-600">-$3,732</span></p>
                        <p className="text-xs text-green-700 mt-2">
                          *In this example, buying performs $3,732 better than renting after 5 years
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Common Pitfalls */}
                <div className="mb-8">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">⚠️ Common Pitfalls to Avoid</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h5 className="font-semibold text-red-900 mb-2">❌ Ignoring Opportunity Cost</h5>
                      <p className="text-red-800 text-sm">
                        Many calculators don't account for what your down payment could earn if invested instead. This is often the biggest factor.
                      </p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h5 className="font-semibold text-red-900 mb-2">❌ Underestimating Costs</h5>
                      <p className="text-red-800 text-sm">
                        Don't forget maintenance, property taxes, insurance, HOA fees, and selling costs. These add up significantly over time.
                      </p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h5 className="font-semibold text-red-900 mb-2">❌ Unrealistic Assumptions</h5>
                      <p className="text-red-800 text-sm">
                        Be conservative with property appreciation and investment returns. Past performance doesn't guarantee future results.
                      </p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h5 className="font-semibold text-red-900 mb-2">❌ Short Time Horizons</h5>
                      <p className="text-red-800 text-sm">
                        Real estate is a long-term investment. Short-term analysis often favors renting due to upfront costs.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Advanced Concepts */}
                <div className="mb-8">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">🔬 Advanced Concepts</h4>
                  
                  <div className="space-y-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h5 className="font-semibold text-gray-900 mb-2">Tax Implications</h5>
                      <p className="text-gray-700 text-sm mb-2">
                        Our calculator includes simplified tax modeling, but real-world taxes are complex:
                      </p>
                      <ul className="text-sm text-gray-700 space-y-1 ml-4">
                        <li>• <strong>Mortgage Interest Deduction:</strong> Reduces taxable income for homeowners</li>
                        <li>• <strong>Property Tax Deduction:</strong> SALT deduction (capped at $10,000)</li>
                        <li>• <strong>Capital Gains:</strong> $250,000 exclusion for primary residence (single)</li>
                        <li>• <strong>Investment Taxes:</strong> Long-term capital gains rates apply to investments</li>
                      </ul>
                    </div>
                    
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h5 className="font-semibold text-gray-900 mb-2">Liquidity Considerations</h5>
                      <p className="text-gray-700 text-sm mb-2">
                        Real estate is less liquid than investments:
                      </p>
                      <ul className="text-sm text-gray-700 space-y-1 ml-4">
                        <li>• <strong>Home Equity:</strong> Tied up until you sell (6-12 months process)</li>
                        <li>• <strong>Investments:</strong> Can be sold in days with minimal fees</li>
                        <li>• <strong>Emergency Access:</strong> Consider HELOCs for home equity access</li>
                      </ul>
                    </div>
                    
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h5 className="font-semibold text-gray-900 mb-2">Risk Factors</h5>
                      <p className="text-gray-700 text-sm mb-2">
                        Both scenarios carry different types of risk:
                      </p>
                      <ul className="text-sm text-gray-700 space-y-1 ml-4">
                        <li>• <strong>Real Estate Risk:</strong> Market crashes, natural disasters, neighborhood decline</li>
                        <li>• <strong>Investment Risk:</strong> Market volatility, sequence of returns</li>
                        <li>• <strong>Concentration Risk:</strong> All eggs in one basket (single property vs diversified portfolio)</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Key Assumptions */}
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">⚙️ Key Assumptions & Parameters</h4>
                  
                  <div className="mb-6">
                    <p className="text-gray-700 text-sm mb-4">
                      The calculator uses several configurable assumptions. Understanding these helps you make more informed decisions:
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h5 className="font-semibold text-gray-900 mb-2">🏠 Property Appreciation</h5>
                      <p className="text-gray-700 text-sm mb-2">Default: 4% annually</p>
                      <p className="text-gray-600 text-xs">
                        Historical average varies by location. Consider local market trends, economic factors, and demographic shifts.
                      </p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h5 className="font-semibold text-gray-900 mb-2">📈 Investment Returns</h5>
                      <p className="text-gray-700 text-sm mb-2">Default: 7% annually</p>
                      <p className="text-gray-600 text-xs">
                        S&P 500 historical average (inflation-adjusted). Conservative estimate for diversified portfolio.
                      </p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h5 className="font-semibold text-gray-900 mb-2">🏠 Rent Increases</h5>
                      <p className="text-gray-700 text-sm mb-2">Default: 3% annually</p>
                      <p className="text-gray-600 text-xs">
                        Typically tracks inflation. Varies by market conditions and local rent control laws.
                      </p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h5 className="font-semibold text-gray-900 mb-2">🏛️ Property Tax Growth</h5>
                      <p className="text-gray-700 text-sm mb-2">Configurable: Reassess vs Cap</p>
                      <p className="text-gray-600 text-xs">
                        Some areas reassess to market value, others cap increases. Check local tax policies.
                      </p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h5 className="font-semibold text-gray-900 mb-2">💰 PMI Removal</h5>
                      <p className="text-gray-700 text-sm mb-2">Configurable: Scheduled vs Appreciation</p>
                      <p className="text-gray-600 text-xs">
                        Can be removed when LTV reaches 80% through payments or appreciation.
                      </p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h5 className="font-semibold text-gray-900 mb-2">🔄 Monthly Compounding</h5>
                      <p className="text-gray-700 text-sm mb-2">Default: Monthly</p>
                      <p className="text-gray-600 text-xs">
                        More accurate than annual compounding for investment calculations.
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <h5 className="font-semibold text-yellow-900 mb-2">⚠️ Sensitivity Analysis</h5>
                    <p className="text-yellow-800 text-sm">
                      Small changes in assumptions can significantly impact results. Try different scenarios:
                    </p>
                    <ul className="text-yellow-800 text-sm mt-2 space-y-1 ml-4">
                      <li>• Conservative: 3% property appreciation, 6% investment returns</li>
                      <li>• Optimistic: 5% property appreciation, 8% investment returns</li>
                      <li>• Market crash: 0% property appreciation, 2% investment returns</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-blue-800 text-sm">
                      <strong>💡 Pro Tip:</strong> Most financial parameters are configurable in the calculator above. 
                      Adjust these values to match your specific market conditions, risk tolerance, and expectations. 
                      The more accurate your inputs, the more reliable your analysis.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};
